import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Alert, Badge, Pagination, Dropdown, Tabs, Tab } from 'react-bootstrap';
import { IconSearch, IconFilter, IconRefreshCw, IconCheck, IconMagic, IconX, IconEdit, IconInfo, IconStop, IconCheckSquare } from '../components/Icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  fetchTechnicalSpecs, 
  fetchTechnicalSpecsNewWave,
  updateDescription, 
  updateEnglishName,
  updateEnglishDesc,
  updateProductName,
  updateSeason,
  updateSeasonBatch,
  confirmTZ, 
  uploadImage, 
  deleteImage,
  swapImages,
  generateDescription,
  applyGeneratedText,
  setFilters, 
  setNewWaveFilters,
  clearFilters, 
  clearNewWaveFilters,
  setPagination,
  setNewWavePagination,
  clearError,
  updatePackageSummary,
  setActiveTab
} from '../store/slices/technicalSpecsSlice';
import { fetchFilterOptions, addToStopList } from '../store/slices/productsSlice';
import { TechnicalSpecProduct } from '../store/slices/technicalSpecsSlice';
import ImagesCell from '../components/TechnicalSpecs/ImagesCell';
import TzGenerateBar from '../components/TechnicalSpecs/TzGenerateBar';
import TzTemplatesModal from '../components/TechnicalSpecs/TzTemplatesModal';
import { TzBinding, TzTemplate } from '../components/TechnicalSpecs/tzResolve';
import PackageSelectorModal from '../components/TechnicalSpecs/PackageSelectorModal';
import ProductInfoPanel from '../components/TechnicalSpecs/ProductInfoPanel';
import ProductHistoryModal from '../components/ProductHistoryModal';
import CheckerModal from '../components/CheckerModal';
import SubjectFilterSelect from '../components/SubjectFilterSelect';
import SourceFilterSelect from '../components/SourceFilterSelect';
import SourceBadge from '../components/SourceBadge';
import { loadCheckerPanelState, saveCheckerPanelState } from '../utils/checkerPanelStorage';
import CatalogChatMenu from '../components/Chat/CatalogChatMenu';
import { translateToEnglish } from '../utils/translation';
import { api, cancelTzGeneration, ensureTzWatchers, forgetTzResult, loadTzJobs, loadTzResults, subscribeTzJobs, TZ_CONFIRMED_WEEK_PLAN } from '../services/api';
import { tzFillDecision } from '../components/TechnicalSpecs/tzFill';

/** Кнопка «Информация о товаре» и список фото WB. Старый пайплайн клал basket-url в thumb_middle_url, новый ingest — только в thumb_url. */
function wbInfoImageUrl(product: TechnicalSpecProduct): string {
  const source = (product.source_code || 'wb').trim();
  if (source !== 'wb') return '';
  const middle = (product.thumb_middle_url || '').trim();
  if (middle) return middle;
  const thumb = (product.thumb_url || '').trim();
  if (!thumb || thumb.startsWith('/uploads/')) return '';
  return thumb;
}

const TechnicalSpecs: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { 
    products, 
    newWaveProducts,
    loading, 
    newWaveLoading,
    error, 
    filters,
    newWaveFilters,
    pagination,
    newWavePagination,
    activeTab
  } = useAppSelector((state) => state.technicalSpecs);
  const { filterOptions } = useAppSelector((state) => state.products);
  const [searchInput, setSearchInput] = useState(filters.search);
  const [excludeInput, setExcludeInput] = useState(filters.exclude_phrase);
  const [pageInput, setPageInput] = useState('');
  const [newWavePageInput, setNewWavePageInput] = useState('');
  const [updatingProducts, setUpdatingProducts] = useState<Set<string>>(new Set());
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  const [translatingProducts, setTranslatingProducts] = useState<Set<string>>(new Set());
  const [generatingDescriptions, setGeneratingDescriptions] = useState<Set<string>>(() => new Set(Object.keys(loadTzJobs())));
  const [genStep, setGenStep] = useState<Record<string, string>>(() => {
    const jobs = loadTzJobs();
    return Object.fromEntries(Object.entries(jobs).map(([id, job]) => [id, job.step || 'В очереди']));
  });
  const [editingNames, setEditingNames] = useState<Record<string, { isEditing: boolean; originalName: string }>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, {
    eng_name?: boolean;
    desc?: boolean;
    eng_desc?: boolean;
    images?: boolean;
  }>>({});
  const [packageModalProduct, setPackageModalProduct] = useState<TechnicalSpecProduct | null>(null);
  
  // Состояния для drag and drop упаковки
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);
  const [draggedPackageInfo, setDraggedPackageInfo] = useState<Record<string, number[]> | null>(null);
  const [draggedPackageFullData, setDraggedPackageFullData] = useState<Record<string, any[]> | null>(null); // Полные данные упаковок для вычисления summary
  // Состояния для drag and drop сезонности
  const [draggedSeasonProductId, setDraggedSeasonProductId] = useState<string | null>(null);
  const [draggedSeasonValue, setDraggedSeasonValue] = useState<string | null>(null);
  const [dragOverProductIds, setDragOverProductIds] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [changingPackagesBatch, setChangingPackagesBatch] = useState<Set<string>>(new Set());
  const [openSeasonDropdowns, setOpenSeasonDropdowns] = useState<Set<string>>(new Set());
  // Локальное состояние для временного хранения выбранных сезонов (до закрытия dropdown)
  const [tempSelectedSeasons, setTempSelectedSeasons] = useState<Record<string, string[]>>({});
  const [updatingSeasons, setUpdatingSeasons] = useState<Set<string>>(new Set());
  const [tzConfirmedWeekCount, setTzConfirmedWeekCount] = useState<number | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [addingToStopList, setAddingToStopList] = useState<Set<string>>(new Set());
  const [historyModalProductId, setHistoryModalProductId] = useState<string | null>(null);
  const [checkerModal, setCheckerModal] = useState<{ show: boolean; initialSku?: string }>(() => {
    const saved = loadCheckerPanelState();
    return { show: saved.open };
  });
  const [tzTemplates, setTzTemplates] = useState<TzTemplate[]>([]);
  const [tzBindings, setTzBindings] = useState<TzBinding[]>([]);
  const [tzOverrides, setTzOverrides] = useState<Record<string, string>>({});
  const [tzGearOpen, setTzGearOpen] = useState(false);

  const reloadTzTemplates = useCallback(async () => {
    const response = await api.getTzTemplates();
    setTzTemplates(response.data?.data?.templates || []);
    setTzBindings(response.data?.data?.bindings || []);
  }, []);

  useEffect(() => {
    reloadTzTemplates().catch(() => {});
  }, [reloadTzTemplates]);
  
  // Refs для хранения значений инпутов (неконтролируемые компоненты)
  const inputRefs = useRef<Record<string, { desc: HTMLTextAreaElement | null; eng_name: HTMLInputElement | null; eng_desc: HTMLTextAreaElement | null }>>({});
  
  // Debounce таймеры для сохранения
  const debounceTimersRef = useRef<Record<string, NodeJS.Timeout>>({});
  const pendingFill = useRef<Record<string, { desc?: string; eng_desc?: string; season?: string; at?: number }>>({});
  const posting = useRef(new Set<string>());
  const genToken = useRef<Record<string, number>>({});

  const clearDescTimers = (productId: string) => {
    const id = String(productId);
    for (const key of [`${productId}_desc`, `${productId}_eng_desc`, `${id}_desc`, `${id}_eng_desc`]) {
      if (!debounceTimersRef.current[key]) continue;
      clearTimeout(debounceTimersRef.current[key]);
      delete debounceTimersRef.current[key];
    }
  };

  const descBusy = (productId: string) => {
    const id = String(productId);
    return posting.current.has(id) || Boolean(loadTzJobs()[id]);
  };
  
  // Отладочный лог для selectedProductId
  useEffect(() => {
    console.log('selectedProductId changed to:', selectedProductId);
  }, [selectedProductId]);

  useEffect(() => {
    api.getTzConfirmedWeekCount().then(({ count }) => setTzConfirmedWeekCount(count)).catch(() => setTzConfirmedWeekCount(null));
  }, []);

  // Deep-link: /technical-specs?search=ARTICLE
  useEffect(() => {
    const q = (searchParams.get('search') || searchParams.get('q') || '').trim();
    if (!q) return;
    setSearchInput(q);
    if (activeTab === 'regular') {
      dispatch(setFilters({ search: q }));
    } else {
      dispatch(setNewWaveFilters({ search: q }));
    }
  }, [searchParams, dispatch, activeTab]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'regular') {
      dispatch(setFilters({ search: searchInput }));
      } else {
        dispatch(setNewWaveFilters({ search: searchInput }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, dispatch, activeTab]);

  // Debounced exclude phrase
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'regular') {
      dispatch(setFilters({ exclude_phrase: excludeInput }));
      } else {
        dispatch(setNewWaveFilters({ exclude_phrase: excludeInput }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [excludeInput, dispatch, activeTab]);

  // Синхронизация локальных инпутов при внешнем сбросе фильтров и смене вкладки
  useEffect(() => {
    if (activeTab === 'regular') {
    setSearchInput(filters.search);
    setExcludeInput(filters.exclude_phrase);
    } else {
      setSearchInput(newWaveFilters.search);
      setExcludeInput(newWaveFilters.exclude_phrase);
    }
  }, [filters.search, filters.exclude_phrase, newWaveFilters.search, newWaveFilters.exclude_phrase, activeTab]);


  // Загрузка опций фильтров при монтировании компонента
  useEffect(() => {
    dispatch(fetchFilterOptions());
  }, [dispatch]);

  // Загрузка данных при изменении фильтров или пагинации
  useEffect(() => {
    if (activeTab === 'regular') {
    dispatch(fetchTechnicalSpecs());
    } else {
      dispatch(fetchTechnicalSpecsNewWave());
    }
  }, [dispatch, activeTab, filters, pagination.page, pagination.limit, newWaveFilters, newWavePagination.page, newWavePagination.limit]);

  // Очистка таймеров при размонтировании
  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach(timer => {
        clearTimeout(timer);
      });
      debounceTimersRef.current = {};
    };
  }, []);

  // Обработчики фильтров
  const handleFilterChange = (field: string, value: string) => {
    if (activeTab === 'regular') {
    dispatch(setFilters({ [field]: value }));
    } else {
      dispatch(setNewWaveFilters({ [field]: value }));
    }
  };

  const handleTzUnconfirmedOnlyChange = (checked: boolean) => {
    if (activeTab === 'regular') {
      dispatch(setFilters({ tz_unconfirmed_only: checked }));
    } else {
      dispatch(setNewWaveFilters({ tz_unconfirmed_only: checked }));
    }
  };

  const handleExcludeStopListChange = (checked: boolean) => {
    if (activeTab === 'regular') {
      dispatch(setFilters({ exclude_stop_list: checked }));
    } else {
      dispatch(setNewWaveFilters({ exclude_stop_list: checked }));
    }
  };

  const handleAddToStopList = useCallback(async (productId: string) => {
    setAddingToStopList(prev => new Set(prev).add(productId));
    try {
      await dispatch(addToStopList(productId)).unwrap();
    } catch (err) {
      console.error('Failed to toggle stop list:', err);
    } finally {
      setAddingToStopList(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  }, [dispatch]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const handlePageChange = (page: number) => {
    if (activeTab === 'regular') {
    dispatch(setPagination({ page }));
    } else {
      dispatch(setNewWavePagination({ page }));
    }
  };

  const handlePageJump = (tab: 'regular' | 'newWave') => {
    const inputValue = tab === 'regular' ? pageInput : newWavePageInput;
    const totalPages = tab === 'regular' ? pagination.totalPages : newWavePagination.totalPages;
    const pageNum = parseInt(inputValue);
    
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      if (tab === 'regular') {
        dispatch(setPagination({ page: pageNum }));
        setPageInput('');
      } else {
        dispatch(setNewWavePagination({ page: pageNum }));
        setNewWavePageInput('');
      }
    } else {
      alert(`Введите номер страницы от 1 до ${totalPages}`);
    }
  };

  const handleTabChange = (tab: 'regular' | 'newWave') => {
    dispatch(setActiveTab(tab));
  };

  // Обработчик обновления английского названия (дебаунс при изменении)
  const handleEnglishNameChange = useCallback((productId: string) => {
    // Очищаем предыдущий таймер для этого продукта
    if (debounceTimersRef.current[`${productId}_eng_name`]) {
      clearTimeout(debounceTimersRef.current[`${productId}_eng_name`]);
    }
    
    // Сохранение с дебаунсом
    debounceTimersRef.current[`${productId}_eng_name`] = setTimeout(() => {
      const input = inputRefs.current[productId]?.eng_name;
      if (input) {
        const eng_name = input.value;
        dispatch(updateEnglishName({ productId, eng_name }));
      }
      delete debounceTimersRef.current[`${productId}_eng_name`];
    }, 2000);
  }, [dispatch]);

  // Обработчик обновления английского названия (немедленное сохранение при blur)
  const handleEnglishNameBlur = useCallback((productId: string) => {
    const input = inputRefs.current[productId]?.eng_name;
    if (!input) return;
    
    const eng_name = input.value;
    
    // Отменяем дебаунс, если он был запущен
    if (debounceTimersRef.current[`${productId}_eng_name`]) {
      clearTimeout(debounceTimersRef.current[`${productId}_eng_name`]);
      delete debounceTimersRef.current[`${productId}_eng_name`];
    }
    
    // Сохраняем сразу
    dispatch(updateEnglishName({ productId, eng_name }));
  }, [dispatch]);

  // Обработчик обновления английского описания (дебаунс при изменении)
  const handleEnglishDescChange = useCallback((productId: string) => {
    forgetTzResult(productId);
    delete pendingFill.current[String(productId)];
    if (descBusy(productId)) return;
    // Очищаем предыдущий таймер для этого продукта
    if (debounceTimersRef.current[`${productId}_eng_desc`]) {
      clearTimeout(debounceTimersRef.current[`${productId}_eng_desc`]);
    }
    
    // Сохранение с дебаунсом
    debounceTimersRef.current[`${productId}_eng_desc`] = setTimeout(() => {
      const textarea = inputRefs.current[productId]?.eng_desc;
      if (textarea) {
        const eng_desc = textarea.value;
        dispatch(updateEnglishDesc({ productId, eng_desc }));
      }
      delete debounceTimersRef.current[`${productId}_eng_desc`];
    }, 2000);
  }, [dispatch]);

  // Обработчик обновления английского описания (немедленное сохранение при blur)
  const handleEnglishDescBlur = useCallback((productId: string) => {
    if (descBusy(productId)) return;
    const textarea = inputRefs.current[productId]?.eng_desc;
    if (!textarea) return;
    
    const eng_desc = textarea.value;
    
    // Отменяем дебаунс, если он был запущен
    if (debounceTimersRef.current[`${productId}_eng_desc`]) {
      clearTimeout(debounceTimersRef.current[`${productId}_eng_desc`]);
      delete debounceTimersRef.current[`${productId}_eng_desc`];
    }
    
    // Сохраняем сразу
    dispatch(updateEnglishDesc({ productId, eng_desc }));
  }, [dispatch]);

  // Обработчик обновления описания (дебаунс при изменении)
  const handleDescChange = useCallback((productId: string) => {
    forgetTzResult(productId);
    delete pendingFill.current[String(productId)];
    if (descBusy(productId)) return;
    // Очищаем предыдущий таймер для этого продукта
    if (debounceTimersRef.current[`${productId}_desc`]) {
      clearTimeout(debounceTimersRef.current[`${productId}_desc`]);
    }
    
    // Сохранение с дебаунсом
    debounceTimersRef.current[`${productId}_desc`] = setTimeout(() => {
      const textarea = inputRefs.current[productId]?.desc;
      if (textarea) {
        const desc = textarea.value;
        dispatch(updateDescription({ productId, desc }));
      }
      delete debounceTimersRef.current[`${productId}_desc`];
    }, 2000);
  }, [dispatch]);

  // Обработчик обновления описания (немедленное сохранение при blur)
  const handleDescBlur = useCallback((productId: string) => {
    if (descBusy(productId)) return;
    const textarea = inputRefs.current[productId]?.desc;
    if (!textarea) return;
    
    const desc = textarea.value;
    
    // Отменяем дебаунс, если он был запущен
    if (debounceTimersRef.current[`${productId}_desc`]) {
      clearTimeout(debounceTimersRef.current[`${productId}_desc`]);
      delete debounceTimersRef.current[`${productId}_desc`];
    }
    
    // Сохраняем сразу
    dispatch(updateDescription({ productId, desc }));
  }, [dispatch]);

  // Обработчик перевода названия
  const handleTranslateName = useCallback(async (productId: string, name: string) => {
    if (!name || name.trim().length === 0) return;
    
    setTranslatingProducts(prev => new Set(prev).add(productId));
    try {
      const translated = await translateToEnglish(name);
      const input = inputRefs.current[productId]?.eng_name;
      if (input) {
        input.value = translated;
        handleEnglishNameBlur(productId);
      }
    } catch (error: any) {
      console.error('Failed to translate name:', error);
      alert('Ошибка перевода: ' + (error.message || 'Не удалось перевести текст'));
    } finally {
      setTranslatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  }, [handleEnglishNameBlur]);

  // Обработчик перевода описания
  const handleTranslateDesc = useCallback(async (productId: string, desc: string) => {
    if (!desc || desc.trim().length === 0) return;
    
    setTranslatingProducts(prev => new Set(prev).add(productId));
    try {
      const translated = await translateToEnglish(desc);
      const textarea = inputRefs.current[productId]?.eng_desc;
      if (textarea) {
        textarea.value = translated;
        handleEnglishDescBlur(productId);
      }
    } catch (error: any) {
      console.error('Failed to translate description:', error);
      alert('Ошибка перевода: ' + (error.message || 'Не удалось перевести текст'));
    } finally {
      setTranslatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  }, [handleEnglishDescBlur]);

  // Функции для работы с сезонами
  const parseSeasons = useCallback((seasonString: string | null | undefined): string[] => {
    if (!seasonString) return [];
    return seasonString.split(',').map(s => s.trim()).filter(Boolean);
  }, []);

  const joinSeasons = useCallback((seasons: string[]): string => {
    return seasons.join(', ');
  }, []);

  // Обработчик изменения сезонности (теперь работает с массивом)
  const handleSeasonChange = useCallback(async (productId: string, seasons: string[]): Promise<void> => {
    setUpdatingSeasons(prev => new Set(prev).add(productId));
    try {
      const seasonString = joinSeasons(seasons);
      await dispatch(updateSeason({ productId, season: seasonString })).unwrap();
    } catch (error) {
      console.error('Failed to update season:', error);
    } finally {
      setUpdatingSeasons(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  }, [dispatch, joinSeasons]);

  // Закрытие dropdown'ов сезонности при клике вне их с сохранением изменений
  useEffect(() => {
    const handleClickOutside = async (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Проверяем, что клик не внутри dropdown'а или его меню
      const isInsideDropdown = target.closest('.dropdown') || 
                               target.closest('.dropdown-menu') ||
                               target.closest('.dropdown-toggle') ||
                               target.closest('[role="menu"]');
      
      if (!isInsideDropdown && openSeasonDropdowns.size > 0) {
        // Закрываем все открытые dropdown'ы и сохраняем изменения
        const promises: Promise<void>[] = [];
        openSeasonDropdowns.forEach(productId => {
          const seasonsToSave = tempSelectedSeasons[productId];
          if (seasonsToSave !== undefined) {
            promises.push(
              handleSeasonChange(productId, seasonsToSave).then(() => {
                // Очищаем временное состояние после сохранения
                setTempSelectedSeasons(prev => {
                  const next = { ...prev };
                  delete next[productId];
                  return next;
                });
              })
            );
          }
        });
        
        // Ждем завершения всех сохранений
        await Promise.all(promises);
        setOpenSeasonDropdowns(new Set());
      }
    };

    if (openSeasonDropdowns.size > 0) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openSeasonDropdowns, tempSelectedSeasons, handleSeasonChange]);

  // Функция для получения цвета одного сезона
  const getSingleSeasonColor = useCallback((season: string): string => {
    switch (season.trim()) {
      case 'ALL SEASONS':
        return '#6c757d'; // серый
      case 'Spring':
        return '#28a745'; // зеленый
      case 'Summer':
        return '#ffc107'; // желтый
      case 'Autumn':
        return '#fd7e14'; // оранжевый
      case 'Winter':
        return '#0dcaf0'; // голубой
      default:
        return '#6c757d';
    }
  }, []);

  // Функция для получения градиента или цвета сезонности
  const getSeasonGradient = useCallback((season: string | null | undefined): string => {
    if (!season) return '#6c757d'; // серый по умолчанию
    
    const seasons = parseSeasons(season);
    if (seasons.length === 0) return '#6c757d';
    
    if (seasons.length === 1) {
      return getSingleSeasonColor(seasons[0]);
    }
    
    // Создаем градиент из цветов выбранных сезонов
    const colors = seasons.map(s => getSingleSeasonColor(s));
    const gradientStops = colors.map((color, index) => {
      const percentage = (index / (colors.length - 1)) * 100;
      return `${color} ${percentage}%`;
    }).join(', ');
    
    return `linear-gradient(90deg, ${gradientStops})`;
  }, [parseSeasons, getSingleSeasonColor]);

  const fillRefs = useCallback((productId: string, result: { desc?: string; eng_desc?: string; season?: string; at?: number }) => {
    const id = String(productId);
    const refs = inputRefs.current[id];
    const token = result.at != null ? String(result.at) : `v:${result.desc || ''}\n${result.eng_desc || ''}`;
    let wrote = false;
    let stamped = false;
    const paint = (field: 'desc' | 'eng_desc', value?: string) => {
      const node = refs?.[field];
      if (value == null) return 'absent' as const;
      if (!node) return 'missing' as const;
      const decision = tzFillDecision(node.dataset.tzToken || '', token, node.value, value);
      if (decision === 'keep') return decision;
      if (node.dataset.tzToken !== token) stamped = true;
      if (decision === 'write') {
        node.value = value;
        wrote = true;
      }
      node.dataset.tzToken = token;
      return decision;
    };
    const descDecision = paint('desc', result.desc);
    const engDecision = paint('eng_desc', result.eng_desc);
    if (descDecision === 'missing' || engDecision === 'missing') pendingFill.current[id] = result;
    else delete pendingFill.current[id];
    if (!wrote && !stamped) return;
    dispatch(applyGeneratedText({
      productId: id,
      ...(descDecision !== 'keep' && descDecision !== 'missing' && result.desc != null ? { desc: result.desc } : {}),
      ...(engDecision !== 'keep' && engDecision !== 'missing' && result.eng_desc != null ? { eng_desc: result.eng_desc } : {}),
      ...(result.season != null && descDecision !== 'keep' && engDecision !== 'keep' ? { season: result.season } : {}),
    }));
  }, [dispatch]);

  const syncTzJobs = useCallback(() => {
    const jobs = loadTzJobs();
    const ids = new Set(Object.keys(jobs));
    posting.current.forEach((id) => ids.add(id));
    setGeneratingDescriptions(ids);
    setGenStep((prev) => {
      const steps: Record<string, string> = {};
      Object.entries(jobs).forEach(([id, job]) => {
        steps[id] = job.step || 'В очереди';
      });
      posting.current.forEach((id) => {
        if (!steps[id]) steps[id] = prev[id] || 'В очереди';
      });
      return steps;
    });
    const running = loadTzJobs();
    Object.entries(loadTzResults()).forEach(([productId, result]) => {
      if (running[productId]) return;
      fillRefs(productId, result);
    });
    Object.entries(pendingFill.current).forEach(([productId, result]) => fillRefs(productId, result));
  }, [fillRefs]);

  useEffect(() => {
    ensureTzWatchers((message) => alert(message));
    const stop = subscribeTzJobs(syncTzJobs);
    syncTzJobs();
    return stop;
  }, [syncTzJobs]);

  useEffect(() => {
    syncTzJobs();
  }, [products, newWaveProducts, syncTzJobs]);

  // Обработчик генерации описания
  const handleGenerateDescription = useCallback(async (productId: string, templateId: string) => {
    const id = String(productId);
    const token = (genToken.current[id] || 0) + 1;
    genToken.current[id] = token;
    clearDescTimers(id);
    forgetTzResult(id);
    delete pendingFill.current[id];
    posting.current.add(id);
    setGeneratingDescriptions(prev => new Set(prev).add(id));
    setGenStep(prev => ({ ...prev, [id]: 'В очереди' }));
    try {
      const result = await dispatch(generateDescription({ productId: id, templateId })).unwrap();
      if (genToken.current[id] !== token) return;
      fillRefs(id, result);
    } catch (error: any) {
      if (genToken.current[id] !== token) return;
      if (error !== 'Отменено') alert(error || 'Не удалось сгенерировать');
    } finally {
      if (genToken.current[id] !== token) return;
      posting.current.delete(id);
      syncTzJobs();
    }
  }, [dispatch, fillRefs, syncTzJobs]);

  const handleCancelGenerate = useCallback((productId: string) => {
    const id = String(productId);
    genToken.current[id] = (genToken.current[id] || 0) + 1;
    posting.current.delete(id);
    void cancelTzGeneration(id);
    syncTzJobs();
  }, [syncTzJobs]);

  // Обработчик подтверждения ТЗ (сохраняет все поля)
  const handleConfirmTZ = useCallback(async (productId: string, confirmed: boolean) => {
    setUpdatingProducts(prev => new Set(prev).add(productId));
    try {
      // Очищаем все таймеры для этого продукта перед сохранением
      Object.keys(debounceTimersRef.current).forEach(key => {
        if (key.startsWith(productId)) {
          clearTimeout(debounceTimersRef.current[key]);
          delete debounceTimersRef.current[key];
        }
      });
      
      // Получаем значения из refs
      const refs = inputRefs.current[productId];
      const desc = refs?.desc?.value || '';
      const eng_name = refs?.eng_name?.value || '';
      const eng_desc = refs?.eng_desc?.value || '';
      
      await dispatch(confirmTZ({ 
        productId, 
        confirmed,
        desc,
        eng_name,
        eng_desc
      })).unwrap();
      forgetTzResult(productId);
      delete pendingFill.current[String(productId)];

      // Оптимистично обновляем счётчик «Подготовлено ТЗ за неделю»
      setTzConfirmedWeekCount(prev => prev !== null ? prev + (confirmed ? 1 : -1) : prev);
      
      // При успешном сохранении очищаем ошибки валидации
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[productId];
        return newErrors;
      });
    } catch (error: any) {
      console.error('Failed to confirm TZ:', error);
      
      // Обрабатываем ошибки валидации
      if (error?.validationErrors) {
        // Очищаем общую ошибку, чтобы показать таблицу
        dispatch(clearError());
        setValidationErrors(prev => ({
          ...prev,
          [productId]: error.validationErrors
        }));
      }
    } finally {
      setUpdatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  }, [dispatch]);

  // Обработчик загрузки изображения
  const handleUploadImage = useCallback(async (productId: string, imageFile: File, imageIndex: number) => {
    setUpdatingProducts(prev => new Set(prev).add(productId));
    try {
      await dispatch(uploadImage({ productId, imageFile, imageIndex })).unwrap();
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setUpdatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  }, [dispatch]);

  // Глобальный обработчик paste для выделенной строки (работает для обеих вкладок: Обычные товары и New Wave)
  useEffect(() => {
    const handleGlobalPaste = async (event: ClipboardEvent) => {
      if (!selectedProductId) return;

      const currentProducts = activeTab === 'regular' ? products : newWaveProducts;
      const product = currentProducts.find(p => p.id === selectedProductId);
      if (!product) return;

      const items = event.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.indexOf('image') !== -1) {
            const file = item.getAsFile();
            if (file) {
              let targetIndex: number | null = null;

              for (let j = 1; j <= 4; j++) {
                const value = (product as any)[`image_url_${j}`];
                if (!value) {
                  targetIndex = j;
                  break;
                }
              }

              if (targetIndex === null && !product.thumb_url) {
                targetIndex = 0;
              }

              if (targetIndex !== null) {
                console.log('Pasting image to selected product', selectedProductId, 'slot', targetIndex);
                await handleUploadImage(selectedProductId, file, targetIndex);
              }
            }
            break;
          }
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
    };
  }, [selectedProductId, activeTab, products, newWaveProducts, handleUploadImage]);

  // Обработчик удаления изображения
  const handleDeleteImage = useCallback(async (productId: string, imageIndex: number) => {
    setUpdatingProducts(prev => new Set(prev).add(productId));
    try {
      await dispatch(deleteImage({ productId, imageIndex })).unwrap();
    } catch (error) {
      console.error('Failed to delete image:', error);
    } finally {
      setUpdatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  }, [dispatch]);

  // Обработчик перестановки изображений
  const handleSwapImages = useCallback(async (productId: string, fromIndex: number, toIndex: number) => {
    setUpdatingProducts(prev => new Set(prev).add(productId));
    try {
      await dispatch(swapImages({ productId, fromIndex, toIndex })).unwrap();
    } catch (error) {
      console.error('Failed to swap images:', error);
    } finally {
      setUpdatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  }, [dispatch]);

  // Обработчики drag and drop для упаковки
  const handleDragStart = useCallback(async (e: React.DragEvent, productId: string) => {
    const product = activeTab === 'regular' 
      ? products.find(p => p.id === productId)
      : newWaveProducts.find(p => p.id === productId);
    if (!product || !product.package_summary || product.package_summary.trim().length === 0) {
      e.preventDefault();
      return;
    }

    try {
      // Загружаем полные данные упаковки
      const res = await api.getProductPackageInfo(productId);
      const data = res.data as any;
      const pkgInfo = data?.data?.packageInfo || {};
      
      // Преобразуем в формат Record<string, number[]>
      const packageInfo: Record<string, number[]> = {};
      Object.entries(pkgInfo).forEach(([type, packages]: [string, any]) => {
        if (Array.isArray(packages)) {
          const selectedIds = packages
            .filter((p: any) => p.selected)
            .map((p: any) => p.id);
          if (selectedIds.length > 0) {
            packageInfo[type] = selectedIds;
          }
        }
      });

      if (Object.keys(packageInfo).length === 0) {
        e.preventDefault();
        return;
      }

      setDraggedProductId(productId);
      setDraggedPackageInfo(packageInfo);
      setDraggedPackageFullData(pkgInfo); // Сохраняем полные данные для вычисления summary
      setIsDragging(true);
      setDragOverProductIds(new Set([productId]));

      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', productId);
    } catch (error) {
      console.error('Failed to load package info for drag:', error);
      e.preventDefault();
    }
  }, [products, newWaveProducts, activeTab]);

  const handleSeasonDragStart = useCallback((e: React.DragEvent, productId: string) => {
    const currentProducts = activeTab === 'regular' ? products : newWaveProducts;
    const product = currentProducts.find(p => p.id === productId);
    if (!product || !product.season || !product.season.trim()) {
      e.preventDefault();
      return;
    }
    setDraggedSeasonProductId(productId);
    setDraggedSeasonValue(product.season ?? '');
    setIsDragging(true);
    setDragOverProductIds(new Set([productId]));
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', productId);
  }, [products, newWaveProducts, activeTab]);

  const handleDragOver = useCallback((e: React.DragEvent, targetProductId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const sourceId = draggedSeasonProductId ?? (draggedPackageInfo ? draggedProductId : null);
    if (!sourceId) return;

    const currentProducts = activeTab === 'regular' ? products : newWaveProducts;
    const sourceIndex = currentProducts.findIndex(p => p.id === sourceId);
    const targetIndex = currentProducts.findIndex(p => p.id === targetProductId);

    if (sourceIndex === -1 || targetIndex === -1) return;

    const startIndex = Math.min(sourceIndex, targetIndex);
    const endIndex = Math.max(sourceIndex, targetIndex);
    const affectedProductIds = currentProducts
      .slice(startIndex, endIndex + 1)
      .map(p => p.id);

    setDragOverProductIds(new Set(affectedProductIds));
  }, [draggedProductId, draggedPackageInfo, draggedSeasonProductId, products, newWaveProducts, activeTab]);

  const handleDragEnd = useCallback(() => {
    setDraggedProductId(null);
    setDraggedPackageInfo(null);
    setDraggedPackageFullData(null);
    setDraggedSeasonProductId(null);
    setDraggedSeasonValue(null);
    setDragOverProductIds(new Set());
    setIsDragging(false);
  }, []);

  // Функция для вычисления package_summary из packageInfo
  // Используем сохраненные полные данные упаковок из handleDragStart
  const calculatePackageSummary = useCallback((packageInfo: Record<string, number[]>, fullPackageData: Record<string, any[]> | null): string => {
    if (!fullPackageData) {
      // Fallback: если полные данные не доступны, возвращаем пустую строку
      // В этом случае package_summary будет обновлен при следующей загрузке данных
      return '';
    }

    const selectedNames: string[] = [];
    Object.entries(packageInfo).forEach(([type, ids]) => {
      const packages = fullPackageData[type] || [];
      ids.forEach(id => {
        const pkg = packages.find((p: any) => p.id === id);
        if (pkg && pkg.name) {
          selectedNames.push(pkg.name);
        }
      });
    });

    return selectedNames.length > 0 ? selectedNames.join(', ') : '';
  }, []);

  // Пакетное применение упаковки
  const handleBatchPackageChange = useCallback(async (productIds: string[], packageInfo: Record<string, number[]>, fullPackageData: Record<string, any[]> | null) => {
    const newChangingBatch = new Set(productIds);
    setChangingPackagesBatch(newChangingBatch);

    // Вычисляем package_summary один раз для всех товаров
    const packageSummary = calculatePackageSummary(packageInfo, fullPackageData);

    const results = await Promise.allSettled(
      productIds.map(productId =>
        api.updateProductPackageInfo(productId, packageInfo)
      )
    );

    // Обновляем Redux state для успешно обновленных товаров
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        const productId = productIds[index];
        dispatch(updatePackageSummary({ productId, packageSummary }));
      } else {
        console.error('Failed to update package for product:', productIds[index], result.reason);
      }
    });

    setChangingPackagesBatch(new Set());
  }, [dispatch, calculatePackageSummary]);

  const handleBatchSeasonChange = useCallback(async (productIds: string[], season: string) => {
    setUpdatingSeasons(prev => {
      const next = new Set(prev);
      productIds.forEach(id => next.add(id));
      return next;
    });
    try {
      await dispatch(updateSeasonBatch({ productIds, season })).unwrap();
    } catch (error) {
      console.error('Failed to update season for some products:', error);
    } finally {
      setUpdatingSeasons(prev => {
        const next = new Set(prev);
        productIds.forEach(id => next.delete(id));
        return next;
      });
    }
  }, [dispatch]);

  const handleDrop = useCallback(async (e: React.DragEvent, targetProductId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const affectedProductIds = Array.from(dragOverProductIds);
    if (affectedProductIds.length === 0) {
      handleDragEnd();
      return;
    }

    if (draggedSeasonProductId != null && draggedSeasonValue != null) {
      await handleBatchSeasonChange(affectedProductIds, draggedSeasonValue);
    } else if (draggedProductId && draggedPackageInfo) {
      await handleBatchPackageChange(affectedProductIds, draggedPackageInfo, draggedPackageFullData);
    }

    handleDragEnd();
  }, [draggedProductId, draggedPackageInfo, draggedPackageFullData, draggedSeasonProductId, draggedSeasonValue, dragOverProductIds, handleBatchPackageChange, handleBatchSeasonChange, handleDragEnd]);

  // Получаем уникальные значения для фильтров
  // Предметы и страны теперь берутся из БД через filterOptions
  const uniqueSubjects = useMemo(() => {
    // Всегда используем filterOptions, если они загружены
    if (filterOptions?.subjects && filterOptions.subjects.length > 0) {
      return filterOptions.subjects;
    }
    // Fallback на данные из products только если filterOptions еще не загружены
    const subjects = products.map(p => ({ id: p.subject_id, name: p.subject_name }));
    return subjects.filter((subject, index, self) => 
      index === self.findIndex(s => s.id === subject.id)
    );
  }, [filterOptions, products]);

  const uniqueCountries = useMemo(() => {
    if (filterOptions?.countries) {
      return filterOptions.countries;
    }
    // Fallback на данные из products, если filterOptions еще не загружены
    return Array.from(new Set(products.map(p => p.country).filter(Boolean)));
  }, [filterOptions, products]);

  // Фильтр предмета
  const currentSubjectId = activeTab === 'regular' ? filters.subject_id : newWaveFilters.subject_id;
  const currentSourceCode = activeTab === 'regular' ? filters.source_code : newWaveFilters.source_code;
  const sourceOptions = filterOptions?.sources || [];

  const selectedProduct = useMemo(() => {
    if (!selectedProductId) return null;
    const p = products.find(x => x.id === selectedProductId) || newWaveProducts.find(x => x.id === selectedProductId);
    if (!p) return null;
    return {
      id: p.id,
      name: p.name ?? '',
      eng_name: p.eng_name ?? '',
      thumb_middle_url: wbInfoImageUrl(p),
      thumb_url: p.thumb_url ?? '',
      url: p.url ?? ''
    };
  }, [selectedProductId, products, newWaveProducts]);

  const formatSubjectName = (subjectName: string) => {
    if (!subjectName) return '-';
    const slashIndex = subjectName.indexOf('/');
    return slashIndex !== -1 ? subjectName.substring(slashIndex + 1) : subjectName;
  };

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">Технические задания</h2>

      {/* Фильтры */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <IconFilter className="me-2" />
              Фильтры и поиск
            </h5>
            <div>
              <Button variant="outline-secondary" size="sm" onClick={() => dispatch(clearFilters())} className="me-2">
                <IconRefreshCw />
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Поиск</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Поиск по артикулу, названию, предмету..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Предмет</Form.Label>
                <SubjectFilterSelect
                  subjects={uniqueSubjects}
                  value={currentSubjectId}
                  onChange={(id) => handleFilterChange('subject_id', id)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Исключить фразы</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="фраза1, фраза2"
                  value={excludeInput}
                  onChange={(e) => setExcludeInput(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Источник</Form.Label>
                <SourceFilterSelect
                  sources={sourceOptions}
                  value={currentSourceCode}
                  onChange={(code) => handleFilterChange('source_code', code)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="tz-unconfirmed-only"
                  label="Только не подтверждённые"
                  checked={activeTab === 'regular' ? filters.tz_unconfirmed_only : newWaveFilters.tz_unconfirmed_only}
                  onChange={(e) => handleTzUnconfirmedOnlyChange(e.target.checked)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="exclude-stop-list"
                  label="Скрыть стоплист"
                  checked={activeTab === 'regular' ? filters.exclude_stop_list : newWaveFilters.exclude_stop_list}
                  onChange={(e) => handleExcludeStopListChange(e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Таблица товаров */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <h5 className="mb-0">
                <IconSearch className="me-2" />
                Технические задания
                {pagination.total > 0 && (
                  <Badge bg="primary" className="ms-2">
                    {pagination.total} товаров
                  </Badge>
                )}
              </h5>
              {tzConfirmedWeekCount !== null && (
                <span className={tzConfirmedWeekCount >= TZ_CONFIRMED_WEEK_PLAN ? 'text-success' : ''}>
                  Подготовлено ТЗ за неделю: ({tzConfirmedWeekCount}/{TZ_CONFIRMED_WEEK_PLAN})
                </span>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm" id="checker-dropdown-tz">
                  <IconCheckSquare size={14} className="me-1" />
                  Чекер
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => { saveCheckerPanelState({ open: true }); setCheckerModal({ show: true }); }}>
                    Открыть тут
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => window.open('/products/checker', '_blank')}>
                    Открыть в новой вкладке
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => window.open('/products/checker', '_blank', 'noopener,noreferrer,width=900,height=700')}>
                    Открыть в новом окне
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Button variant="outline-primary" size="sm" onClick={() => dispatch(fetchTechnicalSpecs())} disabled={loading}>
                <IconRefreshCw className={loading ? 'fa-spin' : ''} />
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Tabs activeKey={activeTab} onSelect={(k) => handleTabChange(k as 'regular' | 'newWave')} className="mb-3">
            <Tab eventKey="regular" title="Обычные товары">
              {activeTab === 'regular' && (
                <>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Загрузка...</span>
              </Spinner>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <Alert variant="warning">
                {typeof error === 'string' ? error : (error as any)?.message || 'Произошла ошибка'}
              </Alert>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-4">
              <Alert variant="info">
                Товары не найдены. Попробуйте изменить параметры поиска.
              </Alert>
            </div>
          ) : (
            <>
              <Table responsive variant="dark" className="table-dark">
                <thead>
                  <tr>
                    <th>SKU / Артикул</th>
                    <th>Название</th>
                    <th>Изображения</th>
                            <th>Упаковка</th>
                    <th>Сезонность</th>
                    <th>Описание</th>
                    <th>Описание (EN)</th>
                    <th>Подтвердить</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                     <tr 
                       key={product.id} 
                       style={{ 
                         minHeight: '150px',
                         backgroundColor: product.stop_list
                           ? 'rgba(73, 80, 87, 0.5)'
                           : product.tz_confirmed 
                             ? (selectedProductId === product.id ? 'rgba(40, 167, 69, 0.3)' : 'rgba(40, 167, 69, 0.15)')
                             : (selectedProductId === product.id ? 'rgba(0, 123, 255, 0.2)' : 'transparent'),
                         opacity: product.stop_list ? 0.75 : 1,
                         cursor: 'default'
                       }}
                       className={`${selectedProductId === product.id ? 'table-row-selected' : ''} ${dragOverProductIds.has(product.id) ? 'drag-over-row' : ''} ${product.stop_list ? 'table-row-stop-list' : ''}`}
                       onClick={(e) => {
                         e.stopPropagation();
                         console.log('Row clicked, setting selectedProductId to:', product.id);
                         setSelectedProductId(product.id);
                       }}
                     >
                      <td onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProductId(product.id);
                      }}>
                        <div className="d-flex align-items-start gap-2">
                        <div>
                          <div className="d-flex align-items-center gap-1">
                          {product.source_code && product.source_code !== 'wb' ? (
                            <SourceBadge
                              code={product.source_code}
                              title={product.source_title}
                              color={product.source_color}
                            />
                          ) : null}
                          <a 
                            href={product.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {product.id}
                          </a>
                          <Dropdown onClick={(e) => e.stopPropagation()}>
                            <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted" title="Открыть в чекере">
                              <IconCheckSquare size={12} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => { saveCheckerPanelState({ open: true }); setCheckerModal({ show: true, initialSku: product.id }); }}>
                                Открыть тут
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank')}>
                                Открыть в новой вкладке
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank', 'noopener,noreferrer,width=900,height=700')}>
                                Открыть в новом окне
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                          <CatalogChatMenu productId={product.id} />
                          </div>
                          {product.article && (
                            <div className="text-muted small mt-1">
                              Артикул: {product.article}
                            </div>
                            )}
                          </div>
                          {wbInfoImageUrl(product) && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductId(product.id);
                                setInfoPanelOpen(true);
                              }}
                              style={{
                                padding: '2px 6px',
                                border: 'none',
                                background: 'transparent'
                              }}
                              title="Информация о товаре"
                            >
                              <IconInfo size={16} className="text-muted" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProductId(product.id);
                        }}
                        style={{ width: '300px', minWidth: '300px', maxWidth: '300px' }}
                      >
                        {editingNames[product.id]?.isEditing ? (
                          <textarea
                            className="form-control form-control-sm"
                            defaultValue={editingNames[product.id].originalName || product.name || ''}
                            onBlur={(e) => {
                              // Добавляем небольшую задержку, чтобы дать возможность обработать клик на других элементах
                              setTimeout(() => {
                                const newName = e.target.value.trim();
                                // Отправляем на бэкенд только если значение изменилось
                                if (newName !== editingNames[product.id].originalName) {
                                  dispatch(updateProductName({ productId: product.id, name: newName }));
                                }
                                setEditingNames(prev => {
                                  const newState = { ...prev };
                                  delete newState[product.id];
                                  return newState;
                                });
                              }, 100);
                            }}
                            onKeyDown={(e) => {
                              // При нажатии Enter (без Shift) сохраняем и закрываем редактирование
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                const newName = e.currentTarget.value.trim();
                                // Отправляем на бэкенд только если значение изменилось
                                if (newName !== editingNames[product.id].originalName) {
                                  dispatch(updateProductName({ productId: product.id, name: newName }));
                                }
                                setEditingNames(prev => {
                                  const newState = { ...prev };
                                  delete newState[product.id];
                                  return newState;
                                });
                                // Убираем фокус с textarea
                                e.currentTarget.blur();
                              }
                            }}
                            rows={3}
                            spellCheck={false}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: '100%', resize: 'vertical' }}
                          />
                        ) : (
                          <div>
                            <div 
                              className="fw-bold" 
                              style={{ width: '100%', cursor: 'pointer', whiteSpace: 'pre-wrap' }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setEditingNames(prev => ({
                                  ...prev,
                                  [product.id]: {
                                    isEditing: true,
                                    originalName: product.name || ''
                                  }
                                }));
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              {product.name}
                            </div>
                            <small className="text-muted">
                              {formatSubjectName(product.subject_name)}
                            </small>
                          </div>
                        )}
                        <div className="mt-2" style={{ position: 'relative' }}>
                          {validationErrors[product.id]?.eng_name && (
                            <Form.Text className="text-danger" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                              Обязательное поле
                            </Form.Text>
                          )}
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder="English name..."
                            defaultValue={product.eng_name || ''}
                            onBlur={() => handleEnglishNameBlur(product.id)}
                            onFocus={(e) => {
                              e.target.select();
                              // Очищаем ошибку при фокусе
                              if (validationErrors[product.id]?.eng_name) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors[product.id]) {
                                    delete newErrors[product.id].eng_name;
                                    if (Object.keys(newErrors[product.id]).length === 0) {
                                      delete newErrors[product.id];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            onChange={() => {
                              // Запускаем дебаунс при изменении
                              handleEnglishNameChange(product.id);
                              // Очищаем ошибку при изменении
                              if (validationErrors[product.id]?.eng_name) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors[product.id]) {
                                    delete newErrors[product.id].eng_name;
                                    if (Object.keys(newErrors[product.id]).length === 0) {
                                      delete newErrors[product.id];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            isInvalid={validationErrors[product.id]?.eng_name}
                            style={{ paddingRight: '35px' }}
                            ref={(el: HTMLInputElement | null) => {
                              if (!inputRefs.current[product.id]) {
                                inputRefs.current[product.id] = { desc: null, eng_name: null, eng_desc: null };
                              }
                              inputRefs.current[product.id].eng_name = el;
                            }}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTranslateName(product.id, product.name);
                            }}
                            disabled={translatingProducts.has(product.id)}
                            style={{ 
                              position: 'absolute', 
                              right: '5px', 
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 10,
                              border: 'none',
                              background: 'transparent',
                              padding: '2px 5px'
                            }}
                          >
                            {translatingProducts.has(product.id) ? (
                              <Spinner size="sm" animation="border" />
                            ) : (
                              <span style={{ color: '#6c757d' }}>
                                <IconMagic size={16} />
                              </span>
                            )}
                          </Button>
                        </div>
                      </td>
                      <td onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProductId(product.id);
                      }}>
                        {validationErrors[product.id]?.images && (
                          <Form.Text className="text-danger" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                            Обязательное поле
                          </Form.Text>
                        )}
                        <div style={{
                          border: validationErrors[product.id]?.images ? '2px solid #dc3545' : 'none',
                          borderRadius: '4px',
                          padding: validationErrors[product.id]?.images ? '2px' : '0'
                      }}>
                        <ImagesCell
                          product={product}
                            onUploadImage={async (productId, imageFile, imageIndex) => {
                              await handleUploadImage(productId, imageFile, imageIndex);
                              // Очищаем ошибку изображений после успешной загрузки
                              if (validationErrors[productId]?.images) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors[productId]) {
                                    delete newErrors[productId].images;
                                    if (Object.keys(newErrors[productId]).length === 0) {
                                      delete newErrors[productId];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                          onDeleteImage={handleDeleteImage}
                          onSwapImages={handleSwapImages}
                        />
                        </div>
                      </td>
                      <td 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProductId(product.id);
                        }}
                        draggable={!!(product.package_summary && product.package_summary.trim().length > 0)}
                        onDragStart={(e) => handleDragStart(e, product.id)}
                        onDragOver={(e) => handleDragOver(e, product.id)}
                        onDrop={(e) => handleDrop(e, product.id)}
                        onDragEnd={handleDragEnd}
                        style={{ 
                          maxWidth: '260px',
                          cursor: (product.package_summary && product.package_summary.trim().length > 0) 
                            ? (isDragging && draggedProductId === product.id ? 'grabbing' : 'grab')
                            : 'default',
                          userSelect: 'none'
                        }}
                        className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
                      >
                        <div className="d-flex align-items-start">
                          <div className="me-2 flex-grow-1 package-summary-cell">
                            {product.package_summary && product.package_summary.trim().length > 0 ? (
                              product.package_summary
                                .split(',')
                                .map(part => part.trim())
                                .filter(Boolean)
                                .map((item, idx, arr) => (
                                  <span key={idx} className="package-summary-item">
                                    {item}
                                    {idx < arr.length - 1 && ', '}
                                  </span>
                                ))
                            ) : (
                              '-'
                            )}
                            {(changingPackagesBatch.has(product.id)) && (
                              <Spinner size="sm" animation="border" variant="primary" className="ms-2" />
                            )}
                          </div>
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              setPackageModalProduct(product);
                            }}
                            onMouseDown={(e) => {
                              // Предотвращаем начало drag при клике на иконку редактирования
                              e.stopPropagation();
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <IconEdit className="text-muted package-edit-icon" />
                          </span>
                        </div>
                      </td>
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProductId(product.id);
                        }}
                        draggable={!!(product.season && product.season.trim())}
                        onDragStart={(e) => handleSeasonDragStart(e, product.id)}
                        onDragOver={(e) => handleDragOver(e, product.id)}
                        onDrop={(e) => handleDrop(e, product.id)}
                        onDragEnd={handleDragEnd}
                        style={{
                          cursor: (product.season && product.season.trim())
                            ? (isDragging && draggedSeasonProductId === product.id ? 'grabbing' : 'grab')
                            : 'default',
                          userSelect: 'none'
                        }}
                        className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
                      >
                        <div className="d-flex align-items-center gap-2">
                        {(() => {
                          const availableSeasons = ['ALL SEASONS', 'Spring', 'Summer', 'Autumn', 'Winter'];
                          const isOpen = openSeasonDropdowns.has(product.id);
                          // Используем временное состояние, если dropdown открыт, иначе - сохраненное значение
                          const selectedSeasons = isOpen && tempSelectedSeasons[product.id] !== undefined
                            ? tempSelectedSeasons[product.id]
                            : parseSeasons(product.season);
                          
                          const toggleSeason = (season: string) => {
                            let newSeasons: string[];
                            
                            if (season === 'ALL SEASONS') {
                              // Если выбираем ALL SEASONS, снимаем все остальные
                              newSeasons = selectedSeasons.includes('ALL SEASONS')
                                ? [] // Если уже выбран, снимаем
                                : ['ALL SEASONS']; // Если не выбран, выбираем только его
                            } else {
                              // Если выбираем обычный сезон
                              if (selectedSeasons.includes(season)) {
                                // Снимаем выбранный сезон
                                newSeasons = selectedSeasons.filter(s => s !== season);
                              } else {
                                // Добавляем сезон и убираем ALL SEASONS если он был выбран
                                newSeasons = [...selectedSeasons.filter(s => s !== 'ALL SEASONS'), season];
                              }
                            }
                            
                            // Обновляем только временное состояние, не сохраняем на сервер
                            setTempSelectedSeasons(prev => ({
                              ...prev,
                              [product.id]: newSeasons
                            }));
                          };

                          const getDisplayText = () => {
                            // Для отображения используем сохраненное значение (не временное)
                            const displaySeasons = parseSeasons(product.season);
                            if (displaySeasons.length === 0) return 'Не выбрано';
                            return displaySeasons.join(', ');
                          };

                          const handleDropdownToggle = async (isOpen: boolean) => {
                            if (isOpen) {
                              // При открытии инициализируем временное состояние из сохраненного значения
                              setTempSelectedSeasons(prev => ({
                                ...prev,
                                [product.id]: parseSeasons(product.season)
                              }));
                              setOpenSeasonDropdowns(prev => new Set(prev).add(product.id));
                            } else {
                              // При закрытии сохраняем изменения на сервер
                              const seasonsToSave = tempSelectedSeasons[product.id];
                              if (seasonsToSave !== undefined) {
                                await handleSeasonChange(product.id, seasonsToSave);
                                // Очищаем временное состояние
                                setTempSelectedSeasons(prev => {
                                  const next = { ...prev };
                                  delete next[product.id];
                                  return next;
                                });
                              }
                              setOpenSeasonDropdowns(prev => {
                                const next = new Set(prev);
                                next.delete(product.id);
                                return next;
                              });
                            }
                          };

                          return (
                            <Dropdown
                              show={isOpen}
                              onToggle={handleDropdownToggle}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Dropdown.Toggle
                          size="sm"
                                variant="dark"
                                style={{
                                  borderColor: '#444',
                                  minWidth: '150px',
                                  textAlign: 'left',
                                  background: (() => {
                                    // Для отображения используем сохраненное значение (не временное)
                                    const displaySeasons = parseSeasons(product.season);
                                    if (displaySeasons.length === 0) return '#2d2d2d';
                                    return displaySeasons.length > 1 
                                      ? getSeasonGradient(product.season)
                                      : getSingleSeasonColor(displaySeasons[0]);
                                  })(),
                                  color: (() => {
                                    const displaySeasons = parseSeasons(product.season);
                                    return displaySeasons.length > 0 ? '#ffffff' : '#6c757d';
                                  })()
                                }}
                              >
                                {getDisplayText()}
                              </Dropdown.Toggle>
                              <Dropdown.Menu
                          style={{
                            backgroundColor: '#2d2d2d',
                            borderColor: '#444',
                                  minWidth: '150px'
                          }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {availableSeasons.map(season => (
                                  <Dropdown.Item
                                    key={season}
                                    as="div"
                                    style={{
                                      backgroundColor: 'transparent',
                                      padding: '0.5rem 1rem',
                                      cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleSeason(season);
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <Form.Check
                                      type="checkbox"
                                      label={season}
                                      checked={selectedSeasons.includes(season)}
                                      onChange={() => toggleSeason(season)}
                                      style={{
                                        color: getSingleSeasonColor(season),
                                        cursor: 'pointer',
                                        margin: 0
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          );
                        })()}
                        {updatingSeasons.has(product.id) && (
                          <Spinner size="sm" animation="border" variant="primary" />
                        )}
                        </div>
                      </td>
                      <td onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProductId(product.id);
                      }}>
                        {validationErrors[product.id]?.desc && (
                          <Form.Text className="text-danger" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                            Обязательное поле
                          </Form.Text>
                        )}
                        <div className={`tz-fieldbox${generatingDescriptions.has(String(product.id)) ? ' is-busy' : ''}`}>
                          <TzGenerateBar
                            subjectId={product.subject_id}
                            sourceCode={product.source_code || 'wb'}
                            templates={tzTemplates}
                            bindings={tzBindings}
                            overrideId={tzOverrides[product.id]}
                            busy={generatingDescriptions.has(String(product.id))}
                            step={genStep[String(product.id)]}
                            onOverride={(templateId) => setTzOverrides((prev) => ({ ...prev, [product.id]: templateId }))}
                            onGenerate={(templateId) => handleGenerateDescription(product.id, templateId)}
                            onCancel={() => handleCancelGenerate(product.id)}
                            onGear={() => setTzGearOpen(true)}
                          />
                          <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Введите техническое задание..."
                            defaultValue={product.desc || ''}
                            onBlur={() => handleDescBlur(product.id)}
                            onFocus={(e) => {
                              e.target.select();
                              // Очищаем ошибку при фокусе
                              if (validationErrors[product.id]?.desc) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors[product.id]) {
                                    delete newErrors[product.id].desc;
                                    if (Object.keys(newErrors[product.id]).length === 0) {
                                      delete newErrors[product.id];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            onChange={() => {
                              // Запускаем дебаунс при изменении
                              handleDescChange(product.id);
                              // Очищаем ошибку при изменении
                              if (validationErrors[product.id]?.desc) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors[product.id]) {
                                    delete newErrors[product.id].desc;
                                    if (Object.keys(newErrors[product.id]).length === 0) {
                                      delete newErrors[product.id];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            isInvalid={validationErrors[product.id]?.desc}
                            style={{ minWidth: '300px' }}
                            ref={(el: HTMLTextAreaElement | null) => {
                              if (!inputRefs.current[product.id]) {
                                inputRefs.current[product.id] = { desc: null, eng_name: null, eng_desc: null };
                              }
                              inputRefs.current[product.id].desc = el;
                            }}
                          />
                        </div>
                      </td>
                      <td onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProductId(product.id);
                      }}>
                        <div>
                          {validationErrors[product.id]?.eng_desc && (
                            <Form.Text className="text-danger" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                              Обязательное поле
                            </Form.Text>
                          )}
                          <div className="tz-fieldbox">
                          <div className="tz-bar" onClick={(e) => e.stopPropagation()}>
                            <span className="tz-bar-hint">С русского</span>
                            <button
                              type="button"
                              className="tz-bar-quiet"
                              disabled={translatingProducts.has(product.id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                const descRef = inputRefs.current[product.id]?.desc;
                                const desc = descRef?.value || product.desc || '';
                                handleTranslateDesc(product.id, desc);
                              }}
                            >
                              {translatingProducts.has(product.id) ? <Spinner size="sm" animation="border" /> : 'Перевести'}
                            </button>
                          </div>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="English description..."
                            defaultValue={product.eng_desc || ''}
                            onBlur={() => handleEnglishDescBlur(product.id)}
                            onFocus={(e) => {
                              e.target.select();
                              // Очищаем ошибку при фокусе
                              if (validationErrors[product.id]?.eng_desc) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors[product.id]) {
                                    delete newErrors[product.id].eng_desc;
                                    if (Object.keys(newErrors[product.id]).length === 0) {
                                      delete newErrors[product.id];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            onChange={() => {
                              // Запускаем дебаунс при изменении
                              handleEnglishDescChange(product.id);
                              // Очищаем ошибку при изменении
                              if (validationErrors[product.id]?.eng_desc) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors[product.id]) {
                                    delete newErrors[product.id].eng_desc;
                                    if (Object.keys(newErrors[product.id]).length === 0) {
                                      delete newErrors[product.id];
                                    }
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            style={{ minWidth: '300px' }}
                            onClick={(e) => e.stopPropagation()}
                            isInvalid={validationErrors[product.id]?.eng_desc}
                            ref={(el: HTMLTextAreaElement | null) => {
                              if (!inputRefs.current[product.id]) {
                                inputRefs.current[product.id] = { desc: null, eng_name: null, eng_desc: null };
                              }
                              inputRefs.current[product.id].eng_desc = el;
                            }}
                          />
                          </div>
                        </div>
                      </td>
                      <td onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProductId(product.id);
                      }}>
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            variant={product.tz_confirmed ? 'danger' : 'outline-secondary'}
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConfirmTZ(product.id, !product.tz_confirmed);
                            }}
                            disabled={updatingProducts.has(product.id) || !!product.stop_list}
                            title={product.stop_list ? 'Недоступно: товар в стоплисте' : undefined}
                          >
                            {updatingProducts.has(product.id) ? (
                              <Spinner size="sm" animation="border" />
                            ) : product.tz_confirmed ? (
                              <IconX size={16} />
                            ) : (
                              <IconCheck size={16} />
                            )}
                          </Button>
                          <Button
                            variant={product.stop_list ? 'warning' : 'danger'}
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToStopList(product.id);
                            }}
                            disabled={addingToStopList.has(product.id) || !!product.tz_confirmed}
                            title={product.tz_confirmed ? 'Недоступно для подтверждённого товара' : (product.stop_list ? 'Убрать из стоплиста' : 'Добавить в стоплист')}
                          >
                            {addingToStopList.has(product.id) ? (
                              <Spinner size="sm" animation="border" />
                            ) : (
                              <IconStop size={16} />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Пагинация */}
              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center mt-3">
                  <Pagination>
                    <Pagination.First 
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.page === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    />
                    
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === pagination.page}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={pagination.page === pagination.totalPages}
                    />
                  </Pagination>
                  <div className="d-flex align-items-center gap-2 ms-3">
                    <Form.Control
                      type="number"
                      size="sm"
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handlePageJump('regular');
                        }
                      }}
                      placeholder="Стр."
                      style={{ maxWidth: '80px', textAlign: 'center' }}
                      min="1"
                      max={pagination.totalPages}
                    />
                    <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                      из {pagination.totalPages}
                    </span>
                    <Button size="sm" variant="outline-secondary" onClick={() => handlePageJump('regular')}>
                      Перейти
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
                </>
              )}
            </Tab>
            <Tab eventKey="newWave" title="New Wave">
              {activeTab === 'newWave' && (
                <>
                  {newWaveLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Загрузка...</span>
                      </Spinner>
                    </div>
                  ) : error ? (
                    <div className="text-center py-4">
                      <Alert variant="warning">
                        {typeof error === 'string' ? error : (error as any)?.message || 'Произошла ошибка'}
                      </Alert>
                    </div>
                  ) : newWaveProducts.length === 0 ? (
                    <div className="text-center py-4">
                      <Alert variant="info">
                        Товары не найдены. Попробуйте изменить параметры поиска.
                      </Alert>
                    </div>
                  ) : (
                    <>
                      <Table responsive variant="dark" className="table-dark">
                        <thead>
                          <tr>
                            <th>SKU / Артикул</th>
                            <th>Название</th>
                            <th>Изображения</th>
                            <th>Упаковка</th>
                            <th>Сезонность</th>
                            <th>Описание</th>
                            <th>Описание (EN)</th>
                            <th>Подтвердить</th>
                          </tr>
                        </thead>
                        <tbody>
                          {newWaveProducts.map((product) => (
                            <tr 
                              key={product.id} 
                              style={{ 
                                minHeight: '150px',
                                backgroundColor: product.stop_list
                                  ? 'rgba(73, 80, 87, 0.5)'
                                  : product.tz_confirmed 
                                    ? (selectedProductId === product.id ? 'rgba(40, 167, 69, 0.3)' : 'rgba(40, 167, 69, 0.15)')
                                    : (selectedProductId === product.id ? 'rgba(0, 123, 255, 0.2)' : 'transparent'),
                                opacity: product.stop_list ? 0.75 : 1,
                                cursor: 'default'
                              }}
                              className={`${selectedProductId === product.id ? 'table-row-selected' : ''} ${dragOverProductIds.has(product.id) ? 'drag-over-row' : ''} ${product.stop_list ? 'table-row-stop-list' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductId(product.id);
                              }}
                            >
                              <td onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductId(product.id);
                              }}>
                                <div className="d-flex align-items-start gap-2">
                                  <div>
                                    <div className="d-flex align-items-center gap-1">
                                    {product.source_code && product.source_code !== 'wb' ? (
                                      <SourceBadge
                                        code={product.source_code}
                                        title={product.source_title}
                                        color={product.source_color}
                                      />
                                    ) : null}
                                    <a 
                                      href={product.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-primary text-decoration-none"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {product.id}
                                    </a>
                                    <Dropdown onClick={(e) => e.stopPropagation()}>
                                      <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted" title="Открыть в чекере">
                                        <IconCheckSquare size={12} />
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => { saveCheckerPanelState({ open: true }); setCheckerModal({ show: true, initialSku: product.id }); }}>
                                          Открыть тут
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank')}>
                                          Открыть в новой вкладке
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank', 'noopener,noreferrer,width=900,height=700')}>
                                          Открыть в новом окне
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                    <CatalogChatMenu productId={product.id} />
                                    </div>
                                    {product.article && (
                                      <div className="text-muted small mt-1">
                                        Артикул: {product.article}
                                      </div>
                                    )}
                                  </div>
                                  {wbInfoImageUrl(product) && (
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedProductId(product.id);
                                        setInfoPanelOpen(true);
                                      }}
                                      style={{
                                        padding: '2px 6px',
                                        border: 'none',
                                        background: 'transparent'
                                      }}
                                      title="Информация о товаре"
                                    >
                                      <IconInfo size={16} className="text-muted" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProductId(product.id);
                                }}
                                style={{ width: '300px', minWidth: '300px', maxWidth: '300px' }}
                              >
                                {editingNames[product.id]?.isEditing ? (
                                  <textarea
                                    className="form-control form-control-sm"
                                    defaultValue={editingNames[product.id].originalName || product.name || ''}
                                    onBlur={(e) => {
                                      setTimeout(() => {
                                        const newName = e.target.value.trim();
                                        if (newName !== editingNames[product.id].originalName) {
                                          dispatch(updateProductName({ productId: product.id, name: newName }));
                                        }
                                        setEditingNames(prev => {
                                          const newState = { ...prev };
                                          delete newState[product.id];
                                          return newState;
                                        });
                                      }, 100);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        const newName = e.currentTarget.value.trim();
                                        if (newName !== editingNames[product.id].originalName) {
                                          dispatch(updateProductName({ productId: product.id, name: newName }));
                                        }
                                        setEditingNames(prev => {
                                          const newState = { ...prev };
                                          delete newState[product.id];
                                          return newState;
                                        });
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    rows={3}
                                    spellCheck={false}
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ width: '100%', resize: 'vertical' }}
                                  />
                                ) : (
                                  <div>
                                    <div 
                                      className="fw-bold" 
                                      style={{ width: '100%', cursor: 'pointer', whiteSpace: 'pre-wrap' }}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditingNames(prev => ({
                                          ...prev,
                                          [product.id]: {
                                            isEditing: true,
                                            originalName: product.name || ''
                                          }
                                        }));
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                    >
                                      {product.name}
                                    </div>
                                    <small className="text-muted">
                                      {formatSubjectName(product.subject_name)}
                                    </small>
                                  </div>
                                )}
                                <div className="mt-2" style={{ position: 'relative' }}>
                                  {validationErrors[product.id]?.eng_name && (
                                    <Form.Text className="text-danger" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                                      Обязательное поле
                                    </Form.Text>
                                  )}
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="English name..."
                                    defaultValue={product.eng_name || ''}
                                    onBlur={() => handleEnglishNameBlur(product.id)}
                                    onFocus={(e) => {
                                      e.target.select();
                                      if (validationErrors[product.id]?.eng_name) {
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          if (newErrors[product.id]) {
                                            delete newErrors[product.id].eng_name;
                                            if (Object.keys(newErrors[product.id]).length === 0) {
                                              delete newErrors[product.id];
                                            }
                                          }
                                          return newErrors;
                                        });
                                      }
                                    }}
                                    onChange={() => {
                                      handleEnglishNameChange(product.id);
                                      if (validationErrors[product.id]?.eng_name) {
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          if (newErrors[product.id]) {
                                            delete newErrors[product.id].eng_name;
                                            if (Object.keys(newErrors[product.id]).length === 0) {
                                              delete newErrors[product.id];
                                            }
                                          }
                                          return newErrors;
                                        });
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    isInvalid={validationErrors[product.id]?.eng_name}
                                    style={{ paddingRight: '35px' }}
                                    ref={(el: HTMLInputElement | null) => {
                                      if (!inputRefs.current[product.id]) {
                                        inputRefs.current[product.id] = { desc: null, eng_name: null, eng_desc: null };
                                      }
                                      inputRefs.current[product.id].eng_name = el;
                                    }}
                                  />
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTranslateName(product.id, product.name);
                                    }}
                                    disabled={translatingProducts.has(product.id)}
                                    style={{ 
                                      position: 'absolute', 
                                      right: '5px', 
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      zIndex: 10,
                                      border: 'none',
                                      background: 'transparent',
                                      padding: '2px 5px'
                                    }}
                                  >
                                    {translatingProducts.has(product.id) ? (
                                      <Spinner size="sm" animation="border" />
                                    ) : (
                                      <span style={{ color: '#6c757d' }}>
                                        <IconMagic size={16} />
                                      </span>
                                    )}
                                  </Button>
                                </div>
                              </td>
                              <td onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductId(product.id);
                              }}>
                                {validationErrors[product.id]?.images && (
                                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                                    Обязательное поле
                                  </Form.Text>
                                )}
                                <div style={{
                                  border: validationErrors[product.id]?.images ? '2px solid #dc3545' : 'none',
                                  borderRadius: '4px',
                                  padding: validationErrors[product.id]?.images ? '2px' : '0'
                                }}>
                                  <ImagesCell
                                    product={product}
                                    onUploadImage={async (productId, imageFile, imageIndex) => {
                                      await handleUploadImage(productId, imageFile, imageIndex);
                                      if (validationErrors[productId]?.images) {
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          if (newErrors[productId]) {
                                            delete newErrors[productId].images;
                                            if (Object.keys(newErrors[productId]).length === 0) {
                                              delete newErrors[productId];
                                            }
                                          }
                                          return newErrors;
                                        });
                                      }
                                    }}
                                    onDeleteImage={handleDeleteImage}
                                    onSwapImages={handleSwapImages}
                                  />
                                </div>
                              </td>
                              <td 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProductId(product.id);
                                }}
                                draggable={!!(product.package_summary && product.package_summary.trim().length > 0)}
                                onDragStart={(e) => handleDragStart(e, product.id)}
                                onDragOver={(e) => handleDragOver(e, product.id)}
                                onDrop={(e) => handleDrop(e, product.id)}
                                onDragEnd={handleDragEnd}
                                style={{ 
                                  maxWidth: '260px',
                                  cursor: (product.package_summary && product.package_summary.trim().length > 0) 
                                    ? (isDragging && draggedProductId === product.id ? 'grabbing' : 'grab')
                                    : 'default',
                                  userSelect: 'none'
                                }}
                                className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
                              >
                                <div className="d-flex align-items-start">
                                  <div className="me-2 flex-grow-1 package-summary-cell">
                                    {product.package_summary && product.package_summary.trim().length > 0 ? (
                                      product.package_summary
                                        .split(',')
                                        .map(part => part.trim())
                                        .filter(Boolean)
                                        .map((item, idx, arr) => (
                                          <span key={idx} className="package-summary-item">
                                            {item}
                                            {idx < arr.length - 1 && ', '}
                                          </span>
                                        ))
                                    ) : (
                                      '-'
                                    )}
                                    {(changingPackagesBatch.has(product.id)) && (
                                      <Spinner size="sm" animation="border" variant="primary" className="ms-2" />
                                    )}
                                  </div>
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPackageModalProduct(product);
                                    }}
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <IconEdit className="text-muted package-edit-icon" />
                                  </span>
                                </div>
                              </td>
                              <td
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProductId(product.id);
                                }}
                                draggable={!!(product.season && product.season.trim())}
                                onDragStart={(e) => handleSeasonDragStart(e, product.id)}
                                onDragOver={(e) => handleDragOver(e, product.id)}
                                onDrop={(e) => handleDrop(e, product.id)}
                                onDragEnd={handleDragEnd}
                                style={{
                                  cursor: (product.season && product.season.trim())
                                    ? (isDragging && draggedSeasonProductId === product.id ? 'grabbing' : 'grab')
                                    : 'default',
                                  userSelect: 'none'
                                }}
                                className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  {(() => {
                                    const availableSeasons = ['ALL SEASONS', 'Spring', 'Summer', 'Autumn', 'Winter'];
                                    const isOpen = openSeasonDropdowns.has(product.id);
                                    const selectedSeasons = isOpen && tempSelectedSeasons[product.id] !== undefined
                                      ? tempSelectedSeasons[product.id]
                                      : parseSeasons(product.season);
                                    
                                    const toggleSeason = (season: string) => {
                                      let newSeasons: string[];
                                      
                                      if (season === 'ALL SEASONS') {
                                        newSeasons = selectedSeasons.includes('ALL SEASONS')
                                          ? []
                                          : ['ALL SEASONS'];
                                      } else {
                                        if (selectedSeasons.includes(season)) {
                                          newSeasons = selectedSeasons.filter(s => s !== season);
                                        } else {
                                          newSeasons = [...selectedSeasons.filter(s => s !== 'ALL SEASONS'), season];
                                        }
                                      }
                                      
                                      setTempSelectedSeasons(prev => ({
                                        ...prev,
                                        [product.id]: newSeasons
                                      }));
                                    };

                                    const getDisplayText = () => {
                                      const displaySeasons = parseSeasons(product.season);
                                      if (displaySeasons.length === 0) return 'Не выбрано';
                                      return displaySeasons.join(', ');
                                    };

                                    const handleDropdownToggle = async (isOpen: boolean) => {
                                      if (isOpen) {
                                        setTempSelectedSeasons(prev => ({
                                          ...prev,
                                          [product.id]: parseSeasons(product.season)
                                        }));
                                        setOpenSeasonDropdowns(prev => new Set(prev).add(product.id));
                                      } else {
                                        const seasonsToSave = tempSelectedSeasons[product.id];
                                        if (seasonsToSave !== undefined) {
                                          await handleSeasonChange(product.id, seasonsToSave);
                                          setTempSelectedSeasons(prev => {
                                            const next = { ...prev };
                                            delete next[product.id];
                                            return next;
                                          });
                                        }
                                        setOpenSeasonDropdowns(prev => {
                                          const next = new Set(prev);
                                          next.delete(product.id);
                                          return next;
                                        });
                                      }
                                    };

                                    return (
                                      <Dropdown
                                        show={isOpen}
                                        onToggle={handleDropdownToggle}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Dropdown.Toggle
                                          size="sm"
                                          variant="dark"
                                          style={{
                                            borderColor: '#444',
                                            minWidth: '150px',
                                            textAlign: 'left',
                                            background: (() => {
                                              const displaySeasons = parseSeasons(product.season);
                                              if (displaySeasons.length === 0) return '#2d2d2d';
                                              return displaySeasons.length > 1 
                                                ? getSeasonGradient(product.season)
                                                : getSingleSeasonColor(displaySeasons[0]);
                                            })(),
                                            color: (() => {
                                              const displaySeasons = parseSeasons(product.season);
                                              return displaySeasons.length > 0 ? '#ffffff' : '#6c757d';
                                            })()
                                          }}
                                        >
                                          {getDisplayText()}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu
                                          style={{
                                            backgroundColor: '#2d2d2d',
                                            borderColor: '#444',
                                            minWidth: '150px'
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {availableSeasons.map(season => (
                                            <Dropdown.Item
                                              key={season}
                                              as="div"
                                              style={{
                                                backgroundColor: 'transparent',
                                                padding: '0.5rem 1rem',
                                                cursor: 'pointer'
                                              }}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleSeason(season);
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                              }}
                                            >
                                              <Form.Check
                                                type="checkbox"
                                                label={season}
                                                checked={selectedSeasons.includes(season)}
                                                onChange={() => toggleSeason(season)}
                                                style={{
                                                  color: getSingleSeasonColor(season),
                                                  cursor: 'pointer',
                                                  margin: 0
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </Dropdown.Item>
                                          ))}
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    );
                                  })()}
                                  {updatingSeasons.has(product.id) && (
                                    <Spinner size="sm" animation="border" variant="primary" />
                                  )}
                                </div>
                              </td>
                              <td onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductId(product.id);
                              }}>
                                {validationErrors[product.id]?.desc && (
                                  <Form.Text className="text-danger" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                                    Обязательное поле
                                  </Form.Text>
                                )}
                                <div className={`tz-fieldbox${generatingDescriptions.has(String(product.id)) ? ' is-busy' : ''}`}>
                                  <TzGenerateBar
                                    subjectId={product.subject_id}
                                    sourceCode={product.source_code || 'wb'}
                                    templates={tzTemplates}
                                    bindings={tzBindings}
                                    overrideId={tzOverrides[product.id]}
                                    busy={generatingDescriptions.has(String(product.id))}
                                    step={genStep[String(product.id)]}
                                    onOverride={(templateId) => setTzOverrides((prev) => ({ ...prev, [product.id]: templateId }))}
                                    onGenerate={(templateId) => handleGenerateDescription(product.id, templateId)}
                                    onCancel={() => handleCancelGenerate(product.id)}
                                    onGear={() => setTzGearOpen(true)}
                                  />
                                  <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="Введите техническое задание..."
                                    defaultValue={product.desc || ''}
                                    onBlur={() => handleDescBlur(product.id)}
                                    onFocus={(e) => {
                                      e.target.select();
                                      if (validationErrors[product.id]?.desc) {
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          if (newErrors[product.id]) {
                                            delete newErrors[product.id].desc;
                                            if (Object.keys(newErrors[product.id]).length === 0) {
                                              delete newErrors[product.id];
                                            }
                                          }
                                          return newErrors;
                                        });
                                      }
                                    }}
                                    onChange={() => {
                                      handleDescChange(product.id);
                                      if (validationErrors[product.id]?.desc) {
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          if (newErrors[product.id]) {
                                            delete newErrors[product.id].desc;
                                            if (Object.keys(newErrors[product.id]).length === 0) {
                                              delete newErrors[product.id];
                                            }
                                          }
                                          return newErrors;
                                        });
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    isInvalid={validationErrors[product.id]?.desc}
                                    style={{ minWidth: '300px' }}
                                    ref={(el: HTMLTextAreaElement | null) => {
                                      if (!inputRefs.current[product.id]) {
                                        inputRefs.current[product.id] = { desc: null, eng_name: null, eng_desc: null };
                                      }
                                      inputRefs.current[product.id].desc = el;
                                    }}
                                  />
                                </div>
                              </td>
                              <td onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductId(product.id);
                              }}>
                                <div>
                                  {validationErrors[product.id]?.eng_desc && (
                                    <Form.Text className="text-danger" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                                      Обязательное поле
                                    </Form.Text>
                                  )}
                                  <div className="tz-fieldbox">
                                  <div className="tz-bar" onClick={(e) => e.stopPropagation()}>
                                    <span className="tz-bar-hint">С русского</span>
                                    <button
                                      type="button"
                                      className="tz-bar-quiet"
                                      disabled={translatingProducts.has(product.id)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const descRef = inputRefs.current[product.id]?.desc;
                                        const desc = descRef?.value || product.desc || '';
                                        handleTranslateDesc(product.id, desc);
                                      }}
                                    >
                                      {translatingProducts.has(product.id) ? <Spinner size="sm" animation="border" /> : 'Перевести'}
                                    </button>
                                  </div>
                                  <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="English description..."
                                    defaultValue={product.eng_desc || ''}
                                    onBlur={() => handleEnglishDescBlur(product.id)}
                                    onFocus={(e) => {
                                      e.target.select();
                                      if (validationErrors[product.id]?.eng_desc) {
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          if (newErrors[product.id]) {
                                            delete newErrors[product.id].eng_desc;
                                            if (Object.keys(newErrors[product.id]).length === 0) {
                                              delete newErrors[product.id];
                                            }
                                          }
                                          return newErrors;
                                        });
                                      }
                                    }}
                                    onChange={() => {
                                      handleEnglishDescChange(product.id);
                                      if (validationErrors[product.id]?.eng_desc) {
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          if (newErrors[product.id]) {
                                            delete newErrors[product.id].eng_desc;
                                            if (Object.keys(newErrors[product.id]).length === 0) {
                                              delete newErrors[product.id];
                                            }
                                          }
                                          return newErrors;
                                        });
                                      }
                                    }}
                                    style={{ minWidth: '300px' }}
                                    onClick={(e) => e.stopPropagation()}
                                    isInvalid={validationErrors[product.id]?.eng_desc}
                                    ref={(el: HTMLTextAreaElement | null) => {
                                      if (!inputRefs.current[product.id]) {
                                        inputRefs.current[product.id] = { desc: null, eng_name: null, eng_desc: null };
                                      }
                                      inputRefs.current[product.id].eng_desc = el;
                                    }}
                                  />
                                  </div>
                                </div>
                              </td>
                              <td onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductId(product.id);
                              }}>
                                <div className="d-flex align-items-center gap-2">
                                  <Button
                                    variant={product.tz_confirmed ? 'danger' : 'outline-secondary'}
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConfirmTZ(product.id, !product.tz_confirmed);
                                    }}
                                    disabled={updatingProducts.has(product.id) || !!product.stop_list}
                                    title={product.stop_list ? 'Недоступно: товар в стоплисте' : undefined}
                                  >
                                    {updatingProducts.has(product.id) ? (
                                      <Spinner size="sm" animation="border" />
                                    ) : product.tz_confirmed ? (
                                      <IconX size={16} />
                                    ) : (
                                      <IconCheck size={16} />
                                    )}
                                  </Button>
                                  <Button
                                    variant={product.stop_list ? 'warning' : 'danger'}
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddToStopList(product.id);
                                    }}
                                    disabled={addingToStopList.has(product.id) || !!product.tz_confirmed}
                                    title={product.tz_confirmed ? 'Недоступно для подтверждённого товара' : (product.stop_list ? 'Убрать из стоплиста' : 'Добавить в стоплист')}
                                  >
                                    {addingToStopList.has(product.id) ? (
                                      <Spinner size="sm" animation="border" />
                                    ) : (
                                      <IconStop size={16} />
                                    )}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>

                      {/* Пагинация */}
                      {newWavePagination.totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center mt-3">
                          <Pagination>
                            <Pagination.First 
                              onClick={() => handlePageChange(1)}
                              disabled={newWavePagination.page === 1}
                            />
                            <Pagination.Prev 
                              onClick={() => handlePageChange(newWavePagination.page - 1)}
                              disabled={newWavePagination.page === 1}
                            />
                            
                            {Array.from({ length: Math.min(5, newWavePagination.totalPages) }, (_, i) => {
                              const pageNum = Math.max(1, Math.min(newWavePagination.totalPages - 4, newWavePagination.page - 2)) + i;
                              return (
                                <Pagination.Item
                                  key={pageNum}
                                  active={pageNum === newWavePagination.page}
                                  onClick={() => handlePageChange(pageNum)}
                                >
                                  {pageNum}
                                </Pagination.Item>
                              );
                            })}
                            
                            <Pagination.Next 
                              onClick={() => handlePageChange(newWavePagination.page + 1)}
                              disabled={newWavePagination.page === newWavePagination.totalPages}
                            />
                            <Pagination.Last 
                              onClick={() => handlePageChange(newWavePagination.totalPages)}
                              disabled={newWavePagination.page === newWavePagination.totalPages}
                            />
                          </Pagination>
                          <div className="d-flex align-items-center gap-2 ms-3">
                            <Form.Control
                              type="number"
                              size="sm"
                              value={newWavePageInput}
                              onChange={(e) => setNewWavePageInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handlePageJump('newWave');
                                }
                              }}
                              placeholder="Стр."
                              style={{ maxWidth: '80px', textAlign: 'center' }}
                              min="1"
                              max={newWavePagination.totalPages}
                            />
                            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                              из {newWavePagination.totalPages}
                            </span>
                            <Button size="sm" variant="outline-secondary" onClick={() => handlePageJump('newWave')}>
                              Перейти
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <PackageSelectorModal
        show={!!packageModalProduct}
        productId={packageModalProduct?.id || null}
        onHide={() => setPackageModalProduct(null)}
        onSaved={(packageSummary) => {
          if (packageModalProduct?.id) {
            dispatch(updatePackageSummary({ productId: packageModalProduct.id, packageSummary }));
          }
        }}
      />

      <TzTemplatesModal
        show={tzGearOpen}
        templates={tzTemplates}
        bindings={tzBindings}
        subjects={uniqueSubjects}
        sources={sourceOptions}
        onHide={() => setTzGearOpen(false)}
        onChanged={reloadTzTemplates}
      />

      <ProductInfoPanel
        show={infoPanelOpen && !!selectedProduct}
        productId={selectedProduct?.id || ''}
        imageUrl={selectedProduct?.thumb_middle_url || selectedProduct?.thumb_url || ''}
        productUrl={selectedProduct?.url ?? ''}
        productName={selectedProduct?.name ?? ''}
        productEngName={selectedProduct?.eng_name ?? ''}
        onUpdateName={(name) => {
          if (selectedProduct?.id) dispatch(updateProductName({ productId: selectedProduct.id, name }));
        }}
        onUpdateEngName={(eng_name) => {
          if (selectedProduct?.id) dispatch(updateEnglishName({ productId: selectedProduct.id, eng_name }));
        }}
        onGenerateEngName={async () => {
          if (selectedProduct?.id && selectedProduct?.name) await handleTranslateName(selectedProduct.id, selectedProduct.name);
        }}
        generatingEngName={!!(selectedProduct?.id && translatingProducts.has(selectedProduct.id))}
        onHide={() => setInfoPanelOpen(false)}
      />

      <ProductHistoryModal
        show={showHistoryModal}
        onHide={() => { setShowHistoryModal(false); setHistoryModalProductId(null); }}
        productId={historyModalProductId || ''}
        productLabel={historyModalProductId ? `ID ${historyModalProductId}` : undefined}
      />

      <CheckerModal
        show={checkerModal.show}
        onHide={() => { saveCheckerPanelState({ open: false }); setCheckerModal({ show: false }); }}
        initialSku={checkerModal.initialSku}
      />
    </Container>
  );
};

export default TechnicalSpecs;
